import { useEffect, useRef, useState } from "react";
import type { BattleLogEvent } from "../types";

const REPLAY_INTERVAL_MS = 700;
/** Short pause after one or many mob hits — keeps multi-mob rounds feeling almost simultaneous. */
const ENEMY_HIT_INTERVAL_MS = 100;
/** Mob debuffs / on-hit effects — faster than hero turns, slower than raw hit batching. */
const ENEMY_FOLLOWUP_INTERVAL_MS = 180;

function enemyUidsFromLog(log: BattleLogEvent[]): Set<string> {
  const init = log.find((e) => e.action === "INIT");
  return new Set((init?.units ?? []).filter((u) => u.team === 1).map((u) => u.uid));
}

function isEnemyActor(actor: string, enemyUids: Set<string>): boolean {
  if (enemyUids.has(actor)) return true;
  return (
    actor.startsWith("enemy")
    || actor.startsWith("dungeon_enemy")
    || actor.startsWith("campaign_enemy")
    || actor.startsWith("catacomb_boss")
    || actor.startsWith("skeleton_")
  );
}

function isEnemyHitEvent(ev: BattleLogEvent, enemyUids: Set<string>): boolean {
  return isEnemyActor(ev.actor, enemyUids) && (ev.action === "ATTACK" || ev.action === "AOE");
}

/** Consecutive mob ATTACK/AOE lines are revealed in one replay step. */
export function enemyHitBatchSize(log: BattleLogEvent[], startIndex: number): number {
  const enemyUids = enemyUidsFromLog(log);
  if (startIndex >= log.length || !isEnemyHitEvent(log[startIndex], enemyUids)) {
    return 1;
  }
  let size = 0;
  for (let i = startIndex; i < log.length; i++) {
    if (isEnemyHitEvent(log[i], enemyUids)) size += 1;
    else break;
  }
  return size;
}

export function replayDelayMs(ev: BattleLogEvent, log?: BattleLogEvent[]): number {
  const enemyUids = log ? enemyUidsFromLog(log) : new Set<string>();
  if (isEnemyHitEvent(ev, enemyUids)) return ENEMY_HIT_INTERVAL_MS;
  if (isEnemyActor(ev.actor, enemyUids)) return ENEMY_FOLLOWUP_INTERVAL_MS;
  return REPLAY_INTERVAL_MS;
}

/**
 * Progressively reveals a pre-computed battle log, one event at a time.
 * The first event (always an INIT snapshot with the real roster) is
 * revealed synchronously so the caller never has to render placeholder
 * units while waiting for the animation to start.
 *
 * Mob hits use a shorter delay and consecutive enemy ATTACK/AOE events
 * on the same initiative round are batched into a single step.
 *
 * Callers should mount a fresh instance per battle (e.g. via a `key` prop)
 * rather than passing a changing `log` into the same instance.
 */
export function useBattleReplay(log: BattleLogEvent[]) {
  const [visibleLog, setVisibleLog] = useState<BattleLogEvent[]>(() =>
    log.slice(0, Math.min(1, log.length)),
  );
  const [done, setDone] = useState(() => log.length <= 1);
  const indexRef = useRef(Math.min(1, log.length));

  useEffect(() => {
    if (indexRef.current >= log.length) {
      setDone(true);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      if (cancelled) return;

      const batch = enemyHitBatchSize(log, indexRef.current);
      indexRef.current += batch;
      setVisibleLog(log.slice(0, indexRef.current));

      if (indexRef.current >= log.length) {
        setDone(true);
        return;
      }

      timer = setTimeout(tick, replayDelayMs(log[indexRef.current], log));
    };

    timer = setTimeout(tick, replayDelayMs(log[indexRef.current], log));

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { visibleLog, done };
}
